import {
  EnvironmentData,
  EnvironmentTypes,
  getContexts,
  getCustomerPortal,
  WorkspaceData
} from '~/api';
import {
  BillingContactInfo,
  BillingInfoData,
  BillingSubscriptionStatus
} from '~/api/types/billingData.ts';
import { getFormattedDate } from '~/i18n/utils.ts';
import { isFeatureEnabled } from '~/utils/features/featureFlag.tsx';
import { FeatureFlagKeys } from '~/api/types/featureFlagData.ts';
import {
  AppDataContextValueType,
  useAppData
} from '~/components/AppData/AppData.tsx';
import { ResourceContextData } from '~/api/types/resourceContextData.ts';
import { Location } from '@solidjs/router';
import {
  AnyAppPath,
  getSlug,
  replacePathParams,
  WorkspacePagePath
} from '~/components/AppRouter/utils.ts';

const getTranslationKey = (path: string): string => `pages.billing.${path}`;

const BILLING_DEFAULT_FALLBACK_CHARACTER = '-';

const DEFAULT_BILLING_PLAN_NAME = 'Pay as you go';

const CUSTOMIZATION_ADDON_NAME = 'Customization Pro';

const CUSTOMIZATION_PRO_ADDON_ID = 'Customization';

const CUSTOMIZATION_ADDON_FEATURE_TRANSLATION_KEYS = [
  `feature_remove_scalekit_watermark_provider`,
  `feature_custom_domains`,
  `feature_custom_email_service_provider`,
  `feature_email_templates`
];

export enum BillingTabTypes {
  DETAILS = 'details',
  USAGE = 'usage'
}

const isBillingEnabled = () => {
  const appData = useAppData();
  if (!appData) {
    return isFeatureEnabled(FeatureFlagKeys.WORKSPACE_BILLING);
  }
  return (
    isFeatureEnabled(FeatureFlagKeys.WORKSPACE_BILLING) &&
    !!appData.workspace?.billingCustomerId
  );
};

const openCustomerPortal = async ({ workspaceId }: { workspaceId: string }) => {
  const { url } = await getCustomerPortal({
    workspaceId: workspaceId
  });
  window.open(url, '_blank');
};

const getBillingDataOrEmptyCharacter = (str?: string) =>
  str || BILLING_DEFAULT_FALLBACK_CHARACTER;

function getFullBillingAddress(contact?: BillingContactInfo): string {
  const parts = [
    contact?.line1 ?? '',
    contact?.line2 ?? '',
    contact?.city ?? '',
    contact?.state ?? '',
    contact?.postalCode ?? '',
    contact?.country ?? ''
  ]
    .map((part) => part?.trim())
    .filter(Boolean); // removes empty strings

  return parts.join(', ');
}

const getActiveSubscription = (billingInfo: BillingInfoData) => {
  return billingInfo.subscriptions.find(
    (subscription) =>
      subscription.status ===
      BillingSubscriptionStatus.BILLING_SUBSCRIPTION_ACTIVE
  );
};

const getFormattedBillingPeriod = (billingInfo?: BillingInfoData) => {
  if (!billingInfo?.currentInvoice) {
    return BILLING_DEFAULT_FALLBACK_CHARACTER;
  }
  if (
    !billingInfo.currentInvoice.billingStartDate ||
    !billingInfo.currentInvoice.billingEndDate
  ) {
    return getBillingDataOrEmptyCharacter();
  }
  return `${getFormattedDate(billingInfo.currentInvoice.billingStartDate)} to ${getFormattedDate(billingInfo.currentInvoice.billingEndDate)}`;
};

const isPaymentOverdue = (workspace: WorkspaceData) => {
  return workspace.extendedInfo?.paymentOverdue;
};

const isFreeQuotaExceededWithoutPaymentMethod = (workspace: WorkspaceData) => {
  return (
    !workspace.extendedInfo?.paymentMethodPresent &&
    workspace.extendedInfo?.freeQuotaExceeded
  );
};

const shouldShowBillingBanner = ({
  environment,
  workspace
}: {
  environment: EnvironmentData;
  workspace: WorkspaceData;
}) => {
  if (!isBillingEnabled()) {
    return false;
  }
  if (environment.type !== EnvironmentTypes.PRODUCTION) {
    return false;
  }
  return (
    isPaymentOverdue(workspace) ||
    isFreeQuotaExceededWithoutPaymentMethod(workspace)
  );
};

const getEnvironmentUsedForBilling = ({
  appData
}: {
  appData: AppDataContextValueType;
}): EnvironmentData => {
  return appData.environments.sort((a, b) => a.id.localeCompare(b.id))[0];
};

const getResourceContextDataForBilling = async ({
  appData
}: {
  appData: AppDataContextValueType;
}): Promise<ResourceContextData> => {
  // this is a hack done to store the billing context
  // against an environment deterministically
  // so that this environment is always used to write and read the data
  const billingEnvironment = getEnvironmentUsedForBilling({ appData });
  if (!billingEnvironment) {
    throw new Error('Failed to select billing environment');
  }
  return await getContexts({
    environmentId: billingEnvironment.id,
    environmentDomain: billingEnvironment.domain
  });
};

const getCustomizationAddonPrice = () => `$99/month`;

const isInBillingPage = ({ location }: { location: Location }) => {
  const billingPageBasePath = replacePathParams(
    getSlug.workspace(WorkspacePagePath.WORKSPACE_BILLING, true) as AnyAppPath,
    {
      tabType: ''
    }
  );
  if (location.pathname.includes(billingPageBasePath)) {
    return true;
  }
};

export {
  getTranslationKey,
  BILLING_DEFAULT_FALLBACK_CHARACTER,
  DEFAULT_BILLING_PLAN_NAME,
  CUSTOMIZATION_ADDON_NAME,
  CUSTOMIZATION_PRO_ADDON_ID,
  CUSTOMIZATION_ADDON_FEATURE_TRANSLATION_KEYS,
  isBillingEnabled,
  openCustomerPortal,
  getBillingDataOrEmptyCharacter,
  getFullBillingAddress,
  getActiveSubscription,
  getFormattedBillingPeriod,
  isPaymentOverdue,
  isFreeQuotaExceededWithoutPaymentMethod,
  shouldShowBillingBanner,
  getEnvironmentUsedForBilling,
  getResourceContextDataForBilling,
  getCustomizationAddonPrice,
  isInBillingPage
};
